import { ArrowRight, Calendar, Monitor, Star, CheckCircle2 } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import { clickCta } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import wendelAvatar from "@/assets/wendel-santos-avatar.webp";


const listVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, staggerChildren: 0.08 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export function AboutSection({
  onCtaClick: _onCtaClick,
  className,
  contentClassName,
  cardClassName,
  cardOverlayClassName,
  profileCardClassName,
  profileCardOverlayClassName,
  hideKicker,
  hideMockupButton,
  meetingButtonPrimary,
  centerContent,
}: {
  onCtaClick?: () => void;
  className?: string;
  contentClassName?: string;
  cardClassName?: string;
  cardOverlayClassName?: string;
  profileCardClassName?: string;
  profileCardOverlayClassName?: string;

  hideKicker?: boolean;
  hideMockupButton?: boolean;
  meetingButtonPrimary?: boolean;
  centerContent?: boolean;
} = {}) {
  return (
    <section
      id="about"
      className={cn(
        "relative isolate overflow-visible bg-background pt-10 pb-6 sm:pt-6 sm:pb-8",
        className
      )}
    >

      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at center, color-mix(in oklab, var(--brand-blue) 10%, transparent), transparent 60%)",
        }}
      />


      <div className={cn("mx-auto mt-6 max-w-7xl px-6 sm:mt-8 lg:px-10", contentClassName)}>
        {/* Outer glass container */}
        <div
          className={cn(
            "relative isolate overflow-hidden rounded-[2rem] border border-border/60 bg-card/40 p-6 backdrop-blur-xl sm:p-10 lg:p-12",
            cardClassName
          )}
          style={{
            clipPath: "inset(0 round 2rem)",
            WebkitMaskImage: "radial-gradient(white, black)",
          }}
        >
          {/* Glass gradient overlay */}
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 -z-10",
              cardOverlayClassName ??
                "bg-gradient-to-br from-brand-blue/15 via-transparent to-[#ec4899]/15"
            )}
          />


          <div
            aria-hidden
            className="pointer-events-none absolute -z-10 -top-32 -left-32 h-72 w-72 rounded-full bg-brand-blue/20 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -z-10 -bottom-32 -right-32 h-72 w-72 rounded-full bg-[#ec4899]/20 blur-3xl"
          />

          <div className="relative z-10 grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:gap-12">
            {/* Left column */}
            <div className={cn("flex flex-col", centerContent && "lg:justify-center")}>
              {!hideKicker && (
                <span className="inline-flex w-fit items-center rounded-full border border-border/60 bg-background/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-blue backdrop-blur">
                  WHY WORK WITH ME
                </span>
              )}

              <div className={cn("space-y-5", hideKicker ? "mt-0" : "mt-6")}>
                <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-4xl !leading-[1.15]">
                  <span className="sm:whitespace-nowrap">You're not hiring a random agency.</span>
                  <br />
                  <span className="text-gradient-cycle text-4xl sm:text-6xl lg:text-6xl">
                    You're working with me.
                  </span>
                </h2>
                <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                  I’m Wendel Santos, founder of Mind Media. I’ve spent 20+ years
                  designing websites, brands, ads, and ecommerce experiences for
                  companies ranging from local businesses to brands with
                  hundreds of thousands of monthly visitors. Mind Media exists
                  for business owners who want a site that looks sharp, loads
                  fast, and actually turns traffic into leads, bookings, and
                  sales.
                </p>
              </div>

              {/* CTA - desktop/tablet only */}
              <div className="mt-8 hidden flex-wrap items-center gap-3 lg:flex">
                {!hideMockupButton && (
                  <a
                    href="#contact"
                    onClick={() => clickCta("Get a free mockup", "about_section")}
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-8 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                  >
                    Get a Free Mockup
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </a>
                )}
                <a
                  href="#contact"
                  onClick={() => clickCta("Book a Meeting", "about_section")}
                  className={cn(
                    "inline-flex h-12 items-center justify-center gap-2 rounded-full px-8 text-sm font-semibold transition-all active:scale-[0.98]",
                    meetingButtonPrimary
                      ? "group bg-gradient-to-r from-brand-blue to-[#ec4899] text-white hover:opacity-90"
                      : "border border-border bg-background text-foreground hover:bg-accent"
                  )}
                >
                  Book a Meeting
                  {meetingButtonPrimary && (
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  )}
                </a>
              </div>
            </div>

            {/* Right column - Profile card */}
            <div className="relative">
              <div
                className={cn(
                  "relative isolate overflow-hidden rounded-[1.75rem] border border-border/60 bg-card/50 p-5 backdrop-blur-xl sm:p-8",
                  profileCardClassName
                )}
                style={{
                  clipPath: "inset(0 round 1.75rem)",
                  WebkitMaskImage: "radial-gradient(white, black)",
                }}
              >
                <div
                  aria-hidden
                  className={cn(
                    "pointer-events-none absolute inset-0",
                    profileCardOverlayClassName ??
                      "bg-gradient-to-br from-brand-blue/10 via-transparent to-[#ec4899]/10"
                  )}
                />


                <div className="flex flex-col items-center text-center">
                  {/* Avatar */}
                  <div className="relative">
                    <div className="relative h-32 w-32 overflow-hidden rounded-full border border-border/60 bg-background/40">
                      <img
                        src={wendelAvatar}
                        alt="Wendel Santos"
                        className="h-full w-full rounded-full object-cover"
                      />
                    </div>
                  </div>


                  <div className="mt-6">
                    <h3 className="text-2xl font-bold text-foreground">
                      Wendel Santos
                    </h3>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
                      Founder · Creative Director
                    </p>
                  </div>

                  <p className="mt-4 text-left text-sm leading-relaxed text-muted-foreground">
                    Former Creative Director at MAINGEAR. Built websites,
                    campaigns, and brand systems for ecommerce, service
                    businesses, and major brands.
                  </p>
                </div>

                {/* Stats row */}
                <motion.div
                  variants={listVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  className="mt-8 rounded-2xl border border-border/60 bg-background/30 px-2 py-4 backdrop-blur sm:p-4"
                >
                  <div className="grid grid-cols-3 divide-x divide-border/60">
                    {[
                      { icon: Calendar, label: "20+ yrs" },
                      { icon: Monitor, label: "Web + Brand" },
                      { icon: Star, label: "5.0 rated" },
                    ].map(({ icon: Icon, label }) => (
                      <motion.div
                        key={label}
                        variants={itemVariants}
                        className="flex flex-col items-center gap-2 px-3 text-center sm:px-4"
                      >
                        <Icon className="h-5 w-5 text-brand-blue" />
                        <span className="whitespace-nowrap text-[11px] font-semibold text-foreground sm:text-sm">
                          {label}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <div className="mt-5 flex items-center gap-3 rounded-2xl px-1">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-blue" />
                  <p className="text-sm text-muted-foreground lg:whitespace-nowrap">
                    Strategy, design, and build handled start to finish.
                  </p>
                </div>
              </div>

              {/* CTA - mobile only */}
              <div className="mt-8 flex flex-col items-center gap-3 lg:hidden">
                {!hideMockupButton && (
                  <a
                    href="#work"
                    onClick={() => clickCta("View my work", "about_section")}
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-8 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                  >
                    View my work
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </a>
                )}
                <a
                  href="#contact"
                  onClick={() => clickCta("Book a Meeting", "about_section")}
                  className={cn(
                    "inline-flex h-12 items-center justify-center gap-2 rounded-full px-8 text-sm font-semibold transition-all active:scale-[0.98]",
                    meetingButtonPrimary
                      ? "group bg-gradient-to-r from-brand-blue to-[#ec4899] text-white hover:opacity-90"
                      : "border border-border bg-background text-foreground hover:bg-accent"
                  )}
                >
                  Book a Meeting
                  {meetingButtonPrimary && (
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  )}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>


    </section>
  );
}

